<template>
  <div class="WebsitePublishedActions">
    <!-- Both actions need the public URL, which is empty until the page resolves a live
         link — without it a copied link or an opened tab would 404, so they stay hidden.
         TODO: show them disabled instead (`:isDisabled="!publishedHref || isBusy"`) once
         the disabled-button designs are improved. -->
    <UITooltip v-if="publishedHref" theme="dark">
      <template #trigger>
        <UIButton
          class="WebsitePublishedActions__icon-button"
          leadIcon="link"
          theme="tertiary"
          size="sm"
          aria-label="Open live preview"
          :isDisabled="isBusy"
          @click="onOpenLive"
        />
      </template>

      <template #content> Open live preview </template>
    </UITooltip>

    <UITooltip v-if="publishedHref" theme="dark">
      <template #trigger>
        <UIButton
          class="WebsitePublishedActions__icon-button"
          leadIcon="linkExternal"
          theme="tertiary"
          size="sm"
          aria-label="Copy link"
          :isDisabled="isBusy"
          @click="onCopyLink"
        />
      </template>

      <template #content> Copy link </template>
    </UITooltip>
    <UIMenuDropdown
      class="WebsitePublishedActions__menu"
      :items="menuItems"
      :isDisabled="isBusy"
      placement="bottom-end"
      @select="onClickedOption"
    >
      <template #toggle>
        <UIButton
          class="WebsitePublishedActions__icon-button"
          leadIcon="dotsVertical"
          theme="tertiary"
          size="sm"
          aria-label="More options"
          :isDisabled="isBusy"
        />
      </template>
    </UIMenuDropdown>

    <ModalDeleteWebsite
      :isOpen="showDeleteModal"
      :isLoading="isDeleting"
      @close="showDeleteModal = false"
      @confirm="onConfirmDelete"
    />

    <ModalUnpublish
      :isOpen="showUnpublishModal"
      :isLoading="isUnpublishing"
      @close="showUnpublishModal = false"
      @confirm="onConfirmUnpublish"
    />
  </div>
</template>

<script lang="ts" setup>
import { UIButton, UITooltip } from '@foundever/ui-lib'

import {
  isWebsiteCardMenuOption,
  useWebsiteCardActions,
} from '@/composables/domain/useWebsiteCardActions'
import ModalDeleteWebsite from '@/views/components/modals/ModalDeleteWebsite.vue'
import ModalUnpublish from '@/views/components/modals/ModalUnpublish.vue'
import UIMenuDropdown from '@/views/components/ui/dropdowns/UIMenuDropdown.vue'

const props = defineProps<{
  websiteId: string
  publishedHref: string
  htmlContent: string
  downloadFileName?: string
}>()

const emit = defineEmits<{
  edit: []
  unpublished: []
  deleted: []
}>()

const {
  publishedHref,
  isBusy,
  isDeleting,
  isUnpublishing,
  showDeleteModal,
  showUnpublishModal,
  menuItems,
  onCopyLink,
  onOpenLive,
  onConfirmUnpublish,
  onConfirmDelete,
  onMenuOptionClick,
} = useWebsiteCardActions({
  variant: 'preview-toolbar',
  websiteId: () => props.websiteId,
  publishedHref: () => props.publishedHref,
  htmlContent: () => props.htmlContent,
  downloadFileName: () => props.downloadFileName,
  // No local publish modal here — bubble "Edit settings" up to the preview panel, which
  // owns the ModalPublishToWeb and opens it in update mode.
  onEdit: () => emit('edit'),
  onUnpublished: () => emit('unpublished'),
  onDeleted: () => emit('deleted'),
})

function onClickedOption(option: { label: string; value: string }) {
  if (isWebsiteCardMenuOption(option)) {
    onMenuOptionClick(option)
  }
}
</script>

<style scoped lang="scss">
.WebsitePublishedActions {
  display: flex;
  align-items: center;
  gap: var(---fds-global-space-xs);
  flex-shrink: 0;

  .WebsitePublishedActions__icon-button {
    border: 1px solid var(--interactive-border-default);
    border-radius: 0.5rem;
  }
}
</style>
